<template>
  <section class="mt-10">
    <div class="w-full">
      <div class="mb-6">
        <h1 class="text-3xl font-semibold text-gray-900">
          Edit Announcement
        </h1>
        <p class="text-sm text-gray-600 mt-1">
          Update the announcement for members.
        </p>
      </div>
      <div
        v-if="loading"
        class="text-sm text-gray-500"
      >
        Loading announcement...
      </div>
      <div
        v-else-if="error"
        class="text-sm text-red-600"
      >
        {{ error }}
      </div>
      <div
        v-else
        class="bg-white border border-gray-200 shadow-sm rounded-xl p-6"
      >
        <form @submit.prevent="updateAnnouncement">
          <div class="grid gap-6 md:grid-cols-2">
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Title</label>
              <input
                v-model="form.title"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                required
                type="text"
              >
            </div>
            <div class="md:col-span-2">
              <label class="block mb-2 text-sm font-medium text-gray-700">Message</label>
              <textarea
                v-model="form.body"
                class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                rows="4"
                required
              />
            </div>
          </div>
          <div class="mt-6 flex items-center gap-3">
            <button
              class="inline-flex items-center rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
              type="submit"
            >
              Save Changes
            </button>
            <router-link
              class="text-sm font-medium text-gray-600 hover:text-gray-900"
              to="/administrator/announcements"
            >
              Cancel
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import {defineComponent, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {Announcement} from '@/types';
import {useHead} from "@vueuse/head";

export default defineComponent({
  name: 'AnnouncementEdit',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const loading = ref(false);
    const error = ref('');
    const form = ref({
      title: '',
      body: '',
    });

    const fetchAnnouncement = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Announcement>(`/announcements/${props.id}`);
        const announcement = response.data;
        form.value = {
          title: announcement.title,
          body: announcement.body,
        };
      } catch (err) {
        console.error(err);
        error.value = 'Failed to load announcement.';
      } finally {
        loading.value = false;
      }
    };

    const updateAnnouncement = async (): Promise<void> => {
      error.value = '';
      try {
        await axios.put(`/announcements/${props.id}`, form.value);
      } catch (err) {
        console.error(err);
        error.value = 'Failed to update announcement.';
      }
    };

    onMounted(fetchAnnouncement);

    watchEffect(() => {
      useHead({
        title: 'Edit Announcement',
      });
    });

    return {
      loading,
      error,
      form,
      updateAnnouncement,
    };
  },
});
</script>
